import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

interface PublicRouteProps {
  children: React.ReactNode;
}

/**
 * PublicRoute - Redirects authenticated users away from public pages (login, forgot password, etc.)
 * If user is already logged in, redirect them to the newsfeed
 */
const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if we're on the 2FA page - if so, allow it even if there's a pending session
        const is2FAPage = window.location.pathname.includes('verify-2fa-code') || 
                         window.location.pathname.includes('two-factor-verify');
        
        if (is2FAPage) {
          // For 2FA page, check if there's a pending 2FA session
          const check2FA = await fetch('/api/check-2fa-pending', {
            credentials: 'include',
          });
          
          if (check2FA.ok) {
            const data = await check2FA.json();
            if (data.pending) {
              // There's a pending 2FA session - allow the page to show
              setIsAuthenticated(false);
              setLoading(false);
              return;
            }
          }
        }
        
        const response = await fetch('/api/auth-check', {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          setIsAuthenticated(data.authenticated || false);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        // Network error or other issue - assume not authenticated
        console.error('PublicRoute: Error checking auth:', error);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If user is authenticated, redirect to newsfeed
  if (isAuthenticated) {
    return <Navigate to="/newsfeed" replace />;
  }

  // User is not authenticated, show the public page
  return <>{children}</>;
};

export default PublicRoute;
